import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { DataContext } from "./index";
import { useTradingColors } from "./tradingColorConfig";
import "./SpotTickerMarquee.css";

// Default popular pairs as immediate fallback so there is never a blank delay
const DEFAULT_PAIRS = [
  { currencyCode: "BTC", baseCurrency: "USDT", ltpValue: "78,182.00", roc: 0.39 },
  { currencyCode: "ETH", baseCurrency: "USDT", ltpValue: "2,284.37", roc: 0.58 },
  { currencyCode: "BNB", baseCurrency: "USDT", ltpValue: "708.04", roc: 0.42 },
  { currencyCode: "SOL", baseCurrency: "USDT", ltpValue: "94.37", roc: -1.40 },
  { currencyCode: "XRP", baseCurrency: "USDT", ltpValue: "1.2509", roc: -0.05 },
  { currencyCode: "DOGE", baseCurrency: "USDT", ltpValue: "0.1637", roc: -4.27 },
  { currencyCode: "ADA", baseCurrency: "USDT", ltpValue: "0.718", roc: 2.12 },
  { currencyCode: "AVAX", baseCurrency: "USDT", ltpValue: "24.90", roc: -2.16 },
  { currencyCode: "LINK", baseCurrency: "USDT", ltpValue: "14.82", roc: 1.05 },
  { currencyCode: "NEAR", baseCurrency: "USDT", ltpValue: "4.097", roc: -6.20 },
  { currencyCode: "CELO", baseCurrency: "USDT", ltpValue: "0.07381", roc: -1.10 },
  { currencyCode: "PEPE", baseCurrency: "USDT", ltpValue: "0.00000484", roc: -1.83 },
  { currencyCode: "ZEC", baseCurrency: "USDT", ltpValue: "1,267.89", roc: 2.25 },
];

const formatPrice = (val, prec = 6) => {
  if (val === null || val === undefined || val === "") return "0.00";
  const num = Number(String(val).replace(/,/g, ""));
  if (isNaN(num)) return String(val);
  if (num >= 1000) {
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (num >= 1) {
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }
  return num.toFixed(Math.min(8, prec || 6));
};

const SpotTickerMarquee = () => {
  const { setCurrencycode, setBasecurrency } = useContext(DataContext) || {};
  const { upColor = "#0ecb81", downColor = "#f6465d" } = useTradingColors();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [pairsList, setPairsList] = useState(() => {
    try {
      const cached = localStorage.getItem("spot_assets");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((p) => p.baseCurrency === "USDT" || p.baseCurrency === "USD").slice(0, 30);
        }
      }
    } catch (e) {}
    return DEFAULT_PAIRS;
  });

  // Sync theme
  useEffect(() => {
    const handleStorage = () => setTheme(localStorage.getItem("theme") || "dark");
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Fetch real assets from API
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const brokerId = localStorage.getItem("BROKERID") || "1";
        const res = await axios.get(
          `https://accounts.paybito.com/CacheService/api/getAssetsData?Name=Assets&BrokerId=${brokerId}`
        );
        if (res.data?.value) {
          const parsed = JSON.parse(res.data.value);
          if (parsed.Values && Array.isArray(parsed.Values) && parsed.Values.length > 0) {
            const usdtPairs = parsed.Values.filter(
              (p) => p.baseCurrency === "USDT" || p.baseCurrency === "USD"
            );
            if (usdtPairs.length > 0) {
              setPairsList(usdtPairs);
            }
          }
        }
      } catch (err) {
        // Fallback to cached assets in localStorage
        try {
          const cached = localStorage.getItem("spot_assets");
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setPairsList(parsed.filter((p) => p.baseCurrency === "USDT" || p.baseCurrency === "USD"));
            }
          }
        } catch (e) {}
      }
    };

    fetchAssets();
    const interval = setInterval(fetchAssets, 15000);
    return () => clearInterval(interval);
  }, []);

  const handlePairClick = (item) => {
    const cur = item.currencyCode || item.currencycode;
    const base = item.baseCurrency || item.basecurrency;
    if (cur && base && setCurrencycode && setBasecurrency) {
      setCurrencycode(cur);
      setBasecurrency(base);
      localStorage.setItem("currency", cur);
      localStorage.setItem("base_currency", base);
    }
  };

  const isLight = theme === "light";

  // Build the list to render (and duplicate for continuous loop)
  const displayItems = useMemo(() => {
    return pairsList.map((item, idx) => {
      const cur = (item.currencyCode || item.currencycode || "").toUpperCase();
      const base = (item.baseCurrency || item.basecurrency || "USDT").toUpperCase();
      const rocNum = parseFloat(item.roc) || 0;
      const isPositive = rocNum >= 0;
      const formattedRoc = `${isPositive ? "+" : ""}${rocNum.toFixed(2)}%`;
      const priceStr = formatPrice(item.ltpValue || item.lasttrade1, item.pricePrecision || 4);

      return {
        id: `${cur}_${base}_${idx}`,
        currencyCode: cur,
        baseCurrency: base,
        rocText: formattedRoc,
        isPositive,
        priceStr,
      };
    });
  }, [pairsList]);

  if (!displayItems || displayItems.length === 0) return null;

  return (
    <div className={`spot-ticker-marquee-container ${isLight ? "light" : "dark"}`}>
      <div className="spot-ticker-marquee-track">
        {/* Set 1 */}
        {displayItems.map((item) => (
          <div
            key={`orig-${item.id}`}
            className="spot-ticker-marquee-item"
            onClick={() => handlePairClick(item)}
            title={`Switch to ${item.currencyCode}/${item.baseCurrency}`}
          >
            <span className="spot-ticker-pair-name">
              {item.currencyCode}/{item.baseCurrency}
            </span>
            <span
              className={`spot-ticker-roc ${item.isPositive ? "up" : "down"}`}
              style={{ color: item.isPositive ? upColor : downColor }}
            >
              {item.rocText}
            </span>
            <span className="spot-ticker-price">{item.priceStr}</span>
            <span className="spot-ticker-divider"></span>
          </div>
        ))}

        {/* Set 2 (for seamless loop) */}
        {displayItems.map((item) => (
          <div
            key={`dup-${item.id}`}
            className="spot-ticker-marquee-item"
            onClick={() => handlePairClick(item)}
            title={`Switch to ${item.currencyCode}/${item.baseCurrency}`}
          >
            <span className="spot-ticker-pair-name">
              {item.currencyCode}/{item.baseCurrency}
            </span>
            <span
              className={`spot-ticker-roc ${item.isPositive ? "up" : "down"}`}
              style={{ color: item.isPositive ? upColor : downColor }}
            >
              {item.rocText}
            </span>
            <span className="spot-ticker-price">{item.priceStr}</span>
            <span className="spot-ticker-divider"></span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpotTickerMarquee;

